import { QuotaAssignmentStrategy } from 'utils/adl-services';
import { isNil } from 'lodash';

export const QUOTA_FILL_MODE = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
};

export const QUOTA_FILL_ORDER = {
  MOST_FILLED: 'mostFilled',
  LEAST_FILLED: 'leastFilled',
};

export const QUOTA_BASED_ON = {
  COUNT: 'count',
  PERCENTAGE: 'percentage',
};

export const QUOTA_ASSIGNMENT_STRATEGY_DEFAULT = undefined;

const DEFAULT_UI_STATE = {
  mode: QUOTA_FILL_MODE.MULTIPLE,
  fillOrder: QUOTA_FILL_ORDER.LEAST_FILLED,
  basedOn: QUOTA_BASED_ON.COUNT,
};

const SINGLE_ASSIGNMENT_CONFIGS = [
  {
    strategy: QuotaAssignmentStrategy.SINGLE_ASSIGNMENT_LEAST_COUNT,
    fillOrder: QUOTA_FILL_ORDER.LEAST_FILLED,
    basedOn: QUOTA_BASED_ON.COUNT,
  },
  {
    strategy: QuotaAssignmentStrategy.SINGLE_ASSIGNMENT_LEAST_PERCENTAGE,
    fillOrder: QUOTA_FILL_ORDER.LEAST_FILLED,
    basedOn: QUOTA_BASED_ON.PERCENTAGE,
  },
  {
    strategy: QuotaAssignmentStrategy.SINGLE_ASSIGNMENT_MOST_COUNT,
    fillOrder: QUOTA_FILL_ORDER.MOST_FILLED,
    basedOn: QUOTA_BASED_ON.COUNT,
  },
  {
    strategy: QuotaAssignmentStrategy.SINGLE_ASSIGNMENT_MOST_PERCENTAGE,
    fillOrder: QUOTA_FILL_ORDER.MOST_FILLED,
    basedOn: QUOTA_BASED_ON.PERCENTAGE,
  },
];

const STRATEGY_TO_UI = new Map([
  [QUOTA_ASSIGNMENT_STRATEGY_DEFAULT, DEFAULT_UI_STATE],
  ...SINGLE_ASSIGNMENT_CONFIGS.map(({ strategy, fillOrder, basedOn }) => [
    strategy,
    {
      mode: QUOTA_FILL_MODE.SINGLE,
      fillOrder,
      basedOn,
    },
  ]),
]);

const UI_TO_STRATEGY = Object.fromEntries(
  SINGLE_ASSIGNMENT_CONFIGS.map(({ strategy, fillOrder, basedOn }) => [
    `${fillOrder}:${basedOn}`,
    strategy,
  ]),
);

export function isSingleAssignmentStrategy(quotaAssignmentStrategy) {
  const normalized = normalizeQuotaAssignmentStrategy(quotaAssignmentStrategy);

  return [
    QuotaAssignmentStrategy.SINGLE_ASSIGNMENT_LEAST_COUNT,
    QuotaAssignmentStrategy.SINGLE_ASSIGNMENT_LEAST_PERCENTAGE,
    QuotaAssignmentStrategy.SINGLE_ASSIGNMENT_MOST_COUNT,
    QuotaAssignmentStrategy.SINGLE_ASSIGNMENT_MOST_PERCENTAGE,
  ].includes(normalized);
}

export function normalizeQuotaAssignmentStrategy(quotaAssignmentStrategy) {
  return quotaAssignmentStrategy || QUOTA_ASSIGNMENT_STRATEGY_DEFAULT;
}

export function parseQuotaAssignmentStrategy(quotaAssignmentStrategy) {
  const normalized = normalizeQuotaAssignmentStrategy(quotaAssignmentStrategy);

  return (
    STRATEGY_TO_UI.get(normalized) ??
    STRATEGY_TO_UI.get(QUOTA_ASSIGNMENT_STRATEGY_DEFAULT)
  );
}

export function buildQuotaAssignmentStrategy({ mode, fillOrder, basedOn }) {
  if (mode === QUOTA_FILL_MODE.MULTIPLE) {
    return QUOTA_ASSIGNMENT_STRATEGY_DEFAULT;
  }

  return UI_TO_STRATEGY[`${fillOrder}:${basedOn}`];
}

export function resolveQuotaAssignmentStrategyForPersist({
  quotaAssignmentStrategy,
  isQuotasEnabled,
  supportsQuotaFillMenu,
}) {
  if (!isQuotasEnabled || !supportsQuotaFillMenu) {
    return undefined;
  }

  return normalizeQuotaAssignmentStrategy(quotaAssignmentStrategy);
}

/**
 * Derives the assigned quota count for an answer or matrix cell.
 * Falls back to `collected` when no previous-revision assignment data exists.
 */
export function getAssignedQuotaCount({
  previousRevisionsAssigned,
  revisionCount = 0,
  collected = 0,
}) {
  if (!isNil(previousRevisionsAssigned)) {
    return previousRevisionsAssigned + revisionCount;
  }

  return collected;
}

/**
 * Derives the quota breakdown columns (existing/assigned/target/remaining counts
 * and progress) for an answer or matrix cell from its minimal quota item, plus the
 * parsed fill-strategy UI state. Centralizes math that was previously duplicated
 * across the answer renderers.
 */
export function deriveQuotaColumns(quotaItem = {}, quotaAssignmentStrategy) {
  const {
    collected,
    remaining,
    revisionCount = 0,
    previousRevisionsAssigned,
  } = quotaItem;

  const assignedQuotaCount = getAssignedQuotaCount({
    previousRevisionsAssigned,
    revisionCount,
    collected,
  });

  const derivedTargetQuota =
    collected !== undefined && remaining !== undefined
      ? assignedQuotaCount + remaining - revisionCount
      : 0;

  return {
    derivedExistingQuotaCount: collected || 0,
    assignedQuotaCount,
    derivedRemainingQuota: remaining - revisionCount || 0,
    derivedTargetQuota,
    quotaProgressPercentage:
      derivedTargetQuota > 0
        ? (assignedQuotaCount / derivedTargetQuota) * 100
        : 0,
    ...parseQuotaAssignmentStrategy(quotaAssignmentStrategy),
  };
}
